/**
 * 组件序列化器
 *
 * Component serializer for ECS components.
 */
import { getComponentTypeName, isEntityRefProperty } from '../Decorators';
import { getSerializationMetadata } from './SerializationDecorators';
import { ValueSerializer } from './ValueSerializer';
import { createLogger } from '../../Utils/Logger';
const logger = createLogger('ComponentSerializer');
export class ComponentSerializer {
    static serialize(component) {
        const metadata = getSerializationMetadata(component);
        if (!metadata)
            return null;
        const componentType = component.constructor;
        const typeName = metadata.options.typeId || getComponentTypeName(componentType);
        const data = {};
        for (const [fieldName, options] of metadata.fields) {
            if (metadata.ignoredFields.has(fieldName))
                continue;
            const fieldKey = typeof fieldName === 'symbol' ? fieldName.toString() : fieldName;
            const value = component[fieldName];
            let serializedValue;
            if (isEntityRefProperty(component, fieldKey)) {
                serializedValue = this.serializeEntityRef(value);
            }
            else if (options.serializer) {
                serializedValue = options.serializer(value);
            }
            else {
                serializedValue = ValueSerializer.serialize(value);
            }
            data[options.alias || fieldKey] = serializedValue;
        }
        return { type: typeName, version: metadata.options.version, data };
    }
    static deserialize(serializedData, componentRegistry, context) {
        const componentClass = componentRegistry.get(serializedData.type);
        if (!componentClass) {
            logger.warn(`Component type not found: ${serializedData.type} | 未找到组件类型: ${serializedData.type}`);
            return null;
        }
        const metadata = getSerializationMetadata(componentClass);
        if (!metadata) {
            logger.warn(`Component ${serializedData.type} is not serializable | 组件 ${serializedData.type} 不可序列化`);
            return null;
        }
        const component = new componentClass();
        for (const [fieldName, options] of metadata.fields) {
            const fieldKey = typeof fieldName === 'symbol' ? fieldName.toString() : fieldName;
            const key = options.alias || fieldKey;
            const serializedValue = serializedData.data[key];
            if (serializedValue === undefined)
                continue;
            if (this.isSerializedEntityRef(serializedValue)) {
                if (context) {
                    const ref = serializedValue.__entityRef;
                    context.registerPendingRef(component, fieldKey, ref.id, ref.guid);
                }
                component[fieldName] = null;
                continue;
            }
            const value = options.deserializer
                ? options.deserializer(serializedValue)
                : ValueSerializer.deserialize(serializedValue);
            component[fieldName] = value;
        }
        return component;
    }
    static serializeComponents(components) {
        return components
            .map(c => this.serialize(c))
            .filter((s) => s !== null);
    }
    static deserializeComponents(serializedComponents, componentRegistry, context) {
        return serializedComponents
            .map(s => this.deserialize(s, componentRegistry, context))
            .filter((c) => c !== null);
    }
    static validateVersion(serializedData, expectedVersion) {
        return serializedData.version === expectedVersion;
    }
    static getSerializationInfo(component) {
        const metadata = getSerializationMetadata(component);
        if (!metadata) {
            return { type: 'unknown', version: 0, fields: [], ignoredFields: [], isSerializable: false };
        }
        const componentType = typeof component === 'function'
            ? component
            : component.constructor;
        return {
            type: metadata.options.typeId || getComponentTypeName(componentType),
            version: metadata.options.version,
            fields: Array.from(metadata.fields.keys()).map(k => typeof k === 'symbol' ? k.toString() : k),
            ignoredFields: Array.from(metadata.ignoredFields).map(k => typeof k === 'symbol' ? k.toString() : k),
            isSerializable: true
        };
    }
    static serializeEntityRef(entity) {
        if (!entity)
            return null;
        return { __entityRef: { id: entity.id, guid: entity.persistentId } };
    }
    static isSerializedEntityRef(value) {
        return typeof value === 'object' && value !== null && '__entityRef' in value;
    }
}
//# sourceMappingURL=ComponentSerializer.js.map